'use client';

import { useFormContext, useWatch } from 'react-hook-form';
import { BuildingIcon } from 'lucide-react';
import SectionHeader from './SectionHeader';
import FieldWrapper from './FieldWrapper';
import InputField from './InputField';
import SelectField from './SelectField';
import { ProviderProfileFormData } from '../types';

const FACILITY_TYPES = [
  { value: 'tertiary_hospital', label: 'Tertiary Hospital' },
  { value: 'secondary_hospital', label: 'Secondary Hospital (General Hospital)' },
  { value: 'primary_health_centre', label: 'Primary Health Centre' },
  { value: 'specialist_hospital', label: 'Specialist Hospital' },
  { value: 'teaching_hospital', label: 'Teaching Hospital' },
  { value: 'private_clinic', label: 'Private Clinic' },
  { value: 'mission_hospital', label: 'Mission / Faith-Based Hospital' },
  { value: 'military_hospital', label: 'Military / Para-Military Hospital' },
  { value: 'gym', label: 'Gym' },
  { value: 'spa', label: 'Spa' },
  { value: 'other', label: 'Other' },
];

const OWNERSHIP_TYPES = [
  { value: 'federal_government', label: 'Federal Government' },
  { value: 'state_government', label: 'State Government' },
  { value: 'local_government', label: 'Local Government' },
  { value: 'private_individual', label: 'Private (Individual)' },
  { value: 'private_corporate', label: 'Private (Corporate)' },
  { value: 'ngo', label: 'NGO / Non-Profit' },
  { value: 'faith_based', label: 'Faith-Based Organisation' },
  { value: 'international', label: 'International Organisation' },
];

const ACCREDITATION_STATUSES = [
  { value: 'fully_accredited', label: 'Fully Accredited' },
  { value: 'provisionally_accredited', label: 'Provisionally Accredited' },
  { value: 'pending', label: 'Accreditation Pending' },
  { value: 'not_accredited', label: 'Not Accredited' },
  { value: 'expired', label: 'Accreditation Expired' },
];

const NIGERIAN_STATES = [
  'Abia','Adamawa','Akwa Ibom','Anambra','Bauchi','Bayelsa','Benue','Borno',
  'Cross River','Delta','Ebonyi','Edo','Ekiti','Enugu','FCT (Abuja)','Gombe',
  'Imo','Jigawa','Kaduna','Kano','Katsina','Kebbi','Kogi','Kwara','Lagos',
  'Nasarawa','Niger','Ogun','Ondo','Osun','Oyo','Plateau','Rivers','Sokoto',
  'Taraba','Yobe','Zamfara',
].map((s) => ({ value: s.toLowerCase().replace(/[^a-z]/g, '_'), label: s }));

export default function Step1HospitalIdentity() {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<ProviderProfileFormData>();

  const e = errors.hospitalIdentity;
  const facilityType = useWatch({
    control,
    name: 'hospitalIdentity.facilityType',
  });
  const isGymOrSpa = facilityType === 'gym' || facilityType === 'spa';

  return (
    <div data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:67:4" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="67" data-component-end-line="270" data-component-file="Step1HospitalIdentity.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-fade-in%22%7D" className="animate-fade-in">
      <SectionHeader data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:68:6" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="68" data-component-end-line="76" data-component-file="Step1HospitalIdentity.tsx" data-component-name="SectionHeader" data-component-content="%7B%22elementName%22%3A%22SectionHeader%22%7D"
        stepNumber={1}
        title={isGymOrSpa ? 'Facility Identity' : 'Hospital Identity'}
        description={isGymOrSpa
          ? 'Provide the core identifying information about your business facility. All fields marked with an asterisk are required.'
          : 'Provide the core identifying information about your healthcare facility. All fields marked with an asterisk are required.'
        }
        icon={<BuildingIcon data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:75:14" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="75" data-component-end-line="75" data-component-file="Step1HospitalIdentity.tsx" data-component-name="BuildingIcon" data-component-content="%7B%22elementName%22%3A%22BuildingIcon%22%7D" size={22} />}
      />

      <div data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:78:6" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="78" data-component-end-line="269" data-component-file="Step1HospitalIdentity.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-5%22%7D" className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FieldWrapper data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:79:8" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="79" data-component-end-line="96" data-component-file="Step1HospitalIdentity.tsx" data-component-name="FieldWrapper" data-component-content="%7B%22elementName%22%3A%22FieldWrapper%22%2C%22className%22%3A%22md%3Acol-span-2%22%7D"
          label="Facility Name"
          htmlFor="facilityName"
          required
          helper={isGymOrSpa ? 'Full legal name of the facility as registered' : 'Full legal name of the healthcare facility as registered'}
          error={e?.facilityName?.message}
          className="md:col-span-2"
        >
          <InputField data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:87:10" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="87" data-component-end-line="95" data-component-file="Step1HospitalIdentity.tsx" data-component-name="InputField" data-component-content="%7B%22elementName%22%3A%22InputField%22%2C%22id%22%3A%22facilityName%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
            id="facilityName"
            placeholder={isGymOrSpa ? 'e.g. FitLife Wellness Gym' : 'e.g. Lagos University Teaching Hospital'}
            hasError={!!e?.facilityName}
            {...register('hospitalIdentity.facilityName', {
              required: 'Facility name is required',
              minLength: { value: 3, message: 'Facility name must be at least 3 characters' },
            })}
          />
        </FieldWrapper>

        <FieldWrapper data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:98:8" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="98" data-component-end-line="113" data-component-file="Step1HospitalIdentity.tsx" data-component-name="FieldWrapper" data-component-content="%7B%22elementName%22%3A%22FieldWrapper%22%7D"
          label="Facility Type"
          htmlFor="facilityType"
          required
          error={e?.facilityType?.message}
        >
          <SelectField data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:104:10" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="104" data-component-end-line="112" data-component-file="Step1HospitalIdentity.tsx" data-component-name="SelectField" data-component-content="%7B%22elementName%22%3A%22SelectField%22%2C%22id%22%3A%22facilityType%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
            id="facilityType"
            placeholder="Select facility type"
            options={FACILITY_TYPES}
            hasError={!!e?.facilityType}
            {...register('hospitalIdentity.facilityType', {
              required: 'Facility type is required',
            })}
          />
        </FieldWrapper>

        <FieldWrapper data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:115:8" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="115" data-component-end-line="130" data-component-file="Step1HospitalIdentity.tsx" data-component-name="FieldWrapper" data-component-content="%7B%22elementName%22%3A%22FieldWrapper%22%7D"
          label="Ownership Type"
          htmlFor="ownershipType"
          required
          error={e?.ownershipType?.message}
        >
          <SelectField data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:121:10" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="121" data-component-end-line="129" data-component-file="Step1HospitalIdentity.tsx" data-component-name="SelectField" data-component-content="%7B%22elementName%22%3A%22SelectField%22%2C%22id%22%3A%22ownershipType%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
            id="ownershipType"
            placeholder="Select ownership type"
            options={OWNERSHIP_TYPES}
            hasError={!!e?.ownershipType}
            {...register('hospitalIdentity.ownershipType', {
              required: 'Ownership type is required',
            })}
          />
        </FieldWrapper>

        {!isGymOrSpa && (
          <FieldWrapper data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:133:10" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="133" data-component-end-line="145" data-component-file="Step1HospitalIdentity.tsx" data-component-name="FieldWrapper" data-component-content="%7B%22elementName%22%3A%22FieldWrapper%22%7D"
            label="NHIA Registration Number"
            htmlFor="registrationNumber"
            helper="If registered with NHIA (National Health Insurance Authority), enter your provider registration number."
            error={e?.registrationNumber?.message}
          >
            <InputField data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:139:12" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="139" data-component-end-line="144" data-component-file="Step1HospitalIdentity.tsx" data-component-name="InputField" data-component-content="%7B%22elementName%22%3A%22InputField%22%2C%22id%22%3A%22registrationNumber%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
              id="registrationNumber"
              placeholder="e.g. NHIA/REG/2019/00123 (optional)"
              hasError={!!e?.registrationNumber}
              {...register('hospitalIdentity.registrationNumber')}
            />
          </FieldWrapper>
        )}

        <FieldWrapper data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:148:8" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="148" data-component-end-line="167" data-component-file="Step1HospitalIdentity.tsx" data-component-name="FieldWrapper" data-component-content="%7B%22elementName%22%3A%22FieldWrapper%22%7D"
          label="Year Established"
          htmlFor="yearEstablished"
          required
          error={e?.yearEstablished?.message}
        >
          <InputField data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:154:10" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="154" data-component-end-line="166" data-component-file="Step1HospitalIdentity.tsx" data-component-name="InputField" data-component-content="%7B%22elementName%22%3A%22InputField%22%2C%22id%22%3A%22yearEstablished%22%2C%22type%22%3A%22number%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
            id="yearEstablished"
            type="number"
            placeholder="e.g. 1987"
            min={1800}
            max={new Date().getFullYear()}
            hasError={!!e?.yearEstablished}
            {...register('hospitalIdentity.yearEstablished', {
              required: 'Year established is required',
              min: { value: 1800, message: 'Year must be 1800 or later' },
              max: { value: new Date().getFullYear(), message: 'Year cannot be in the future' },
            })}
          />
        </FieldWrapper>

        <FieldWrapper data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:169:8" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="169" data-component-end-line="185" data-component-file="Step1HospitalIdentity.tsx" data-component-name="FieldWrapper" data-component-content="%7B%22elementName%22%3A%22FieldWrapper%22%2C%22className%22%3A%22md%3Acol-span-2%22%7D"
          label="Physical Address"
          htmlFor="physicalAddress"
          required
          helper="Street address, landmark, and local government area"
          error={e?.physicalAddress?.message}
          className="md:col-span-2"
        >
          <InputField data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:177:10" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="177" data-component-end-line="184" data-component-file="Step1HospitalIdentity.tsx" data-component-name="InputField" data-component-content="%7B%22elementName%22%3A%22InputField%22%2C%22id%22%3A%22physicalAddress%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
            id="physicalAddress"
            placeholder="e.g. 1 University Road, Surulere, Lagos"
            hasError={!!e?.physicalAddress}
            {...register('hospitalIdentity.physicalAddress', {
              required: 'Physical address is required',
            })}
          />
        </FieldWrapper>

        <FieldWrapper data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:187:8" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="187" data-component-end-line="202" data-component-file="Step1HospitalIdentity.tsx" data-component-name="FieldWrapper" data-component-content="%7B%22elementName%22%3A%22FieldWrapper%22%7D"
          label="State / Province"
          htmlFor="state"
          required
          error={e?.state?.message}
        >
          <SelectField data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:193:10" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="193" data-component-end-line="201" data-component-file="Step1HospitalIdentity.tsx" data-component-name="SelectField" data-component-content="%7B%22elementName%22%3A%22SelectField%22%2C%22id%22%3A%22state%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
            id="state"
            placeholder="Select state"
            options={NIGERIAN_STATES}
            hasError={!!e?.state}
            {...register('hospitalIdentity.state', {
              required: 'State is required',
            })}
          />
        </FieldWrapper>

        <FieldWrapper data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:204:8" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="204" data-component-end-line="218" data-component-file="Step1HospitalIdentity.tsx" data-component-name="FieldWrapper" data-component-content="%7B%22elementName%22%3A%22FieldWrapper%22%7D"
          label="Country"
          htmlFor="country"
          required
          error={e?.country?.message}
        >
          <InputField data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:210:10" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="210" data-component-end-line="217" data-component-file="Step1HospitalIdentity.tsx" data-component-name="InputField" data-component-content="%7B%22elementName%22%3A%22InputField%22%2C%22id%22%3A%22country%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
            id="country"
            placeholder="e.g. Nigeria"
            hasError={!!e?.country}
            {...register('hospitalIdentity.country', {
              required: 'Country is required',
            })}
          />
        </FieldWrapper>

        <FieldWrapper data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:220:8" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="220" data-component-end-line="232" data-component-file="Step1HospitalIdentity.tsx" data-component-name="FieldWrapper" data-component-content="%7B%22elementName%22%3A%22FieldWrapper%22%7D"
          label="GPS Coordinates"
          htmlFor="gpsCoordinates"
          helper="Latitude and longitude (optional but recommended for mapping)"
          error={e?.gpsCoordinates?.message}
        >
          <InputField data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:226:10" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="226" data-component-end-line="231" data-component-file="Step1HospitalIdentity.tsx" data-component-name="InputField" data-component-content="%7B%22elementName%22%3A%22InputField%22%2C%22id%22%3A%22gpsCoordinates%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
            id="gpsCoordinates"
            placeholder="e.g. 6.5244° N, 3.3792° E"
            hasError={!!e?.gpsCoordinates}
            {...register('hospitalIdentity.gpsCoordinates')}
          />
        </FieldWrapper>

        {!isGymOrSpa && (
          <>
            <FieldWrapper data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:236:12" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="236" data-component-end-line="251" data-component-file="Step1HospitalIdentity.tsx" data-component-name="FieldWrapper" data-component-content="%7B%22elementName%22%3A%22FieldWrapper%22%7D"
              label="Accreditation Status"
              htmlFor="accreditationStatus"
              required
              error={e?.accreditationStatus?.message}
            >
              <SelectField data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:242:14" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="242" data-component-end-line="250" data-component-file="Step1HospitalIdentity.tsx" data-component-name="SelectField" data-component-content="%7B%22elementName%22%3A%22SelectField%22%2C%22id%22%3A%22accreditationStatus%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                id="accreditationStatus"
                placeholder="Select accreditation status"
                options={ACCREDITATION_STATUSES}
                hasError={!!e?.accreditationStatus}
                {...register('hospitalIdentity.accreditationStatus', {
                  required: 'Accreditation status is required',
                })}
              />
            </FieldWrapper>

            <FieldWrapper data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:253:12" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="253" data-component-end-line="266" data-component-file="Step1HospitalIdentity.tsx" data-component-name="FieldWrapper" data-component-content="%7B%22elementName%22%3A%22FieldWrapper%22%2C%22className%22%3A%22md%3Acol-span-2%22%7D"
              label="Accrediting Body"
              htmlFor="accreditingBody"
              helper="Name of the body that issued accreditation (if applicable)"
              error={e?.accreditingBody?.message}
              className="md:col-span-2"
            >
              <InputField data-component-id="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx:260:14" data-component-path="src/app/provider-profile-form/components/Step1HospitalIdentity.tsx" data-component-line="260" data-component-end-line="265" data-component-file="Step1HospitalIdentity.tsx" data-component-name="InputField" data-component-content="%7B%22elementName%22%3A%22InputField%22%2C%22id%22%3A%22accreditingBody%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                id="accreditingBody"
                placeholder="e.g. National Health Insurance Authority (NHIA), HEFAMAA"
                hasError={!!e?.accreditingBody}
                {...register('hospitalIdentity.accreditingBody')}
              />
            </FieldWrapper>
          </>
        )}
      </div>
    </div>
  );
}